import { useEffect, useRef, useState } from "react";
import {
  ConnectionErrorCode,
  type ConnectionError,
  type StreamStatus,
} from "..";
import { GameLaunchStatus } from "../constants/webrtc";

const TURN_FETCH_TIMEOUT_MS = 8000;
const TURN_MAX_ATTEMPTS = 5;

const DISCONNECTED_STATUSES = [
  GameLaunchStatus.STATUS_GAMERUNNING_DISCONNECTED,
  GameLaunchStatus.STATUS_LAUNCHING_DISCONNECTED,
];

interface UseTurnConfigurationProps {
  streamURL: string;
  streamStatus?: StreamStatus;
  onError?: (error: ConnectionError) => void;
}

export const useTurnConfiguration = ({
  streamURL,
  streamStatus,
  onError,
}: UseTurnConfigurationProps) => {
  const [turnConfiguration, setTurnConfiguration] = useState<Record<
    string,
    unknown
  > | null>(null);

  const isFetchingRef = useRef(false);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (
      turnConfiguration ||
      isFetchingRef.current ||
      attemptsRef.current >= TURN_MAX_ATTEMPTS
    )
      return;

    if (
      DISCONNECTED_STATUSES.includes(streamStatus?.status as GameLaunchStatus)
    ) {
      return;
    }

    const steamInstallingStatus = GameLaunchStatus.STATUS_INSTALLING;
    if (
      !streamStatus?.status ||
      !(
        streamStatus.status === steamInstallingStatus ||
        Object.values(GameLaunchStatus).indexOf(
          streamStatus.status as GameLaunchStatus,
        ) > Object.values(GameLaunchStatus).indexOf(steamInstallingStatus)
      )
    ) {
      console.log(
        `Current stream status (${streamStatus?.status}) does not require TURN configuration. Skipping fetch.`,
      );
      return;
    }

    console.log("Fetching TURN configuration...");
    isFetchingRef.current = true;

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      TURN_FETCH_TIMEOUT_MS,
    );

    const fetchTurnConfiguration = async () => {
      try {
        const response = await fetch(`${streamURL}/turn`, {
          signal: controller.signal,
        });
        const configuration = await response.json();
        setTurnConfiguration(configuration);
      } catch (error) {
        attemptsRef.current += 1;
        if ((error as Error).name === "AbortError") {
          console.error(
            `TURN configuration fetch timed out after ${TURN_FETCH_TIMEOUT_MS}ms (attempt ${attemptsRef.current}/${TURN_MAX_ATTEMPTS})`,
          );
        } else {
          console.error(
            `Failed to fetch TURN configuration (attempt ${attemptsRef.current}/${TURN_MAX_ATTEMPTS})`,
          );
        }
        if (attemptsRef.current >= TURN_MAX_ATTEMPTS) {
          onError?.({
            code: ConnectionErrorCode.TURN_FETCH_FAILED,
            message: `Failed to fetch TURN configuration after ${TURN_MAX_ATTEMPTS} attempts`,
          });
        }
      } finally {
        clearTimeout(timeoutId);
        isFetchingRef.current = false;
      }
    };

    fetchTurnConfiguration();
  }, [streamStatus]);

  return { turnConfiguration };
};
